import { toast } from 'react-toastify'
import getConfig from 'next/config'
const { publicRuntimeConfig } = getConfig()
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Client } from '@hapi/nes/lib/client'
import { format } from 'date-fns'
import Link from 'next/link'
import { getKnoxFirmwareRequests } from '../utils/licensesAPIs'

const licenserServer = publicRuntimeConfig.licenseApiBaseUrl.replace('https://', '')
let licenseServerUrl =
  process.env.NODE_ENV !== 'development' ? `wss://${licenserServer}` : 'ws://localhost:8950'

const EfotaOnPremResourceContext = createContext()

const EfotaOnPremResourceProvider = ({ children }) => {
  const nesClientRef = useRef(null)
  if (nesClientRef.current === null) {
    nesClientRef.current = new Client(licenseServerUrl)
  }

  useEffect(() => {
    async function initNes(nesClient) {
      try {
        nesClient.onConnect = () => {
          console.info(`Data export notify connected. Socket id: ${nesClient.id}`)
        }
        nesClient.onDisconnect = (willReconnect, log) => {
          console.info(
            `Data export notify disconnected. Reason: ${log.reason}. Socket id: ${nesClient.id}`
          )
          if (willReconnect) {
            console.info('Will attempt to reconnect...')
          }
        }
        nesClient.onError = (err) => {
          console.error('Data export notify unexpected error', err.stack)
        }
        await nesClient.connect({ option: { timeout: 10000 } })
      } catch (err) {
        console.error(err)
      }
    }
    initNes(nesClientRef.current)
  }, [])
  return (
    <EfotaOnPremResourceContext.Provider value={nesClientRef.current}>
      {children}
    </EfotaOnPremResourceContext.Provider>
  )
}

const EfotaOnPremResourceNotify = () => {
  const nesClient = useContext(EfotaOnPremResourceContext)
  const [firmwareRequests, setFirmwareRequests] = useState([])
  const requestsExpiryInDays = 7

  const knoxFirmwareRequests = async (customerLicense) => {
    const { data, error } = await getKnoxFirmwareRequests(customerLicense)
    if (!data?.requestFirmwares || error) {
      setFirmwareRequests([])
    }
    if (data?.requestFirmwares) setFirmwareRequests(data.requestFirmwares)
  }

  const isSubscribedToPath = (path) => {
    return nesClient.subscriptions().includes(path)
  }

  const subscribeEfotaFirmWareRequest = async (userUUID) => {
    const subscriptionPath = `/api/v1/efota/onPrem/requestFirmwareCallback/${userUUID}`
    if (!isSubscribedToPath(subscriptionPath)) {
      await nesClient.subscribe(subscriptionPath, (payload) => {
        if (publicRuntimeConfig.deploymentEnv !== 'euprd') {
          console.debug('Received data export notify message: %o', payload)
        }
        if (payload.response.customerLicense) {
          knoxFirmwareRequests(payload.response.customerLicense)
          toast.success(efotaNotifContent(payload.response.customerLicense), {
            autoClose: false
          })
        }
      })
    }
  }

  const unsubscribeEfotaFirmWareRequest = async ({ userUUID }) => {
    const subscriptionPath = `/api/v1/efota/onPrem/requestFirmwareCallback/${userUUID}`
    if (isSubscribedToPath(subscriptionPath)) {
      await nesClient.unsubscribe(subscriptionPath)
    }
  }

  return {
    subscribeEfotaFirmWareRequest,
    unsubscribeEfotaFirmWareRequest,
    firmwareRequests,
    setFirmwareRequests,
    requestsExpiryInDays,
    knoxFirmwareRequests
  }
}

const getEfotaDateTime = () => {
  return format(new Date(), 'dd MMM yyyy, HH:mm ')
}

const efotaNotifContent = (customerLicense) => (
  <div>
    <div>
      <strong>File download now available</strong>
    </div>
    <span>Your Knox E-FOTA On-Premises firmware files are ready for download.</span>
    <div>
      <span>{getEfotaDateTime()}</span>
      <Link
        href={`/en/knox-efota-on-premises-resources/download-firmware-files?licenseKey=${customerLicense}`}
      >
        <p className="text-right">
          <strong>View Files</strong>
        </p>
      </Link>
    </div>
  </div>
)

export { EfotaOnPremResourceProvider, EfotaOnPremResourceNotify }
